import { type FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { bannersByRegionIdSelector } from 'shared/selectors/banners/bannersByRegionIdSelector';
import { getFilterLocation } from 'shared/selectors/filters/getFilterLocation';
import { newbuildingsPlusBlocksPikSelector } from 'shared/selectors/specialPromosBlocks/newbuildingsPlusBlocksPikSelector';
import { extractUndefined } from 'shared/utils/extractUndefined';

import { type AnimationDurationScheme } from '../SliderProgressBar';

import { Carousel, type CarouselBanner } from './Carousel';
import { type BadgesTypes } from './internal/components/Badges';
import { trackMainCarouselProgressDotClick } from './internal/tracking/trackMainCarouselProgressDotClick';
import { trackMainCarouselSelectApartmentClick } from './internal/tracking/trackMainCarouselSelectApartmentClick';
import { trackShowMainCarouselSection } from './internal/tracking/trackShowMainCarouselSection';

export const CarouselMainContainer: FC = () => {
  const bannersByRegionId = useSelector(bannersByRegionIdSelector);
  const selectedRegion = useSelector(getFilterLocation);

  const newbuildingsPlusBlocksPik = useSelector(newbuildingsPlusBlocksPikSelector);

  const [banners, intervals] = useMemo(() => {
    const newbuildingsPlusBlocksPikMap = new Map(newbuildingsPlusBlocksPik.map(pik => [pik.newbuildingId, pik]));

    const carouselBanners = Array.of<CarouselBanner>();
    const carouselIntervals: AnimationDurationScheme = {
      // По умолчанию баннер показываем 4 секунды
      default: 4_000,
    };

    bannersByRegionId.forEach((banner, index) => {
      const badges = Array.of<BadgesTypes>();

      if (newbuildingsPlusBlocksPikMap.has(banner.jkId)) {
        badges.push('seriaPlus');
      }

      const carouselBanner: CarouselBanner = {
        ...banner,
        badges,
        disclaimer: extractUndefined(banner.disclaimer),
        hasCommercialProperty: !!banner.hasCommercialProperty,
        label: extractUndefined(banner.label),
        logoUrl: extractUndefined(banner.logoUrl),
        video: extractUndefined(banner.video),
      };

      carouselBanners.push(carouselBanner);

      // Если это первый баннер и у баннера есть видео, то слайд должен показываться 14 секунд
      if (index === 0 && carouselBanner.video) {
        carouselIntervals[index] = 14_000;
      }
      // Если у баннера есть видео, то он должен показываться 8 секунд
      else if (carouselBanner.video) {
        carouselIntervals[index] = 8_000;
      }
    });

    return [carouselBanners, carouselIntervals];
  }, [bannersByRegionId, newbuildingsPlusBlocksPik]);

  const handleView = useCallback(() => trackShowMainCarouselSection(banners), [banners]);

  const handleDotClick = useCallback(
    (index: number) => trackMainCarouselProgressDotClick(banners[index].id, index),
    [banners],
  );

  const handleSelectApartmentClick = useCallback(
    (newbuildingId: number, index: number) => trackMainCarouselSelectApartmentClick(newbuildingId, index),
    [],
  );

  return (
    <Carousel
      banners={banners}
      interval={intervals}
      key={selectedRegion}
      testId="main-carousel"
      onDotClick={handleDotClick}
      onSelectApartmentClick={handleSelectApartmentClick}
      onView={handleView}
    />
  );
};
